import { useContext, useEffect, useRef } from 'react';
import { ScreenLockContext } from '../components/ScreenLockProvider';

/**
 * When active, applies classname(s) to the html element to prevent users from scrolling the page.
 * @param isActive Whether the lock is active
 * @param hidePageScrollbarGutter Optional per-call override for whether the page's scrollbar gutter is hidden.
 * This only impacts users that have scrollbars always on in their OS settings. When the gutter is kept, the
 * `scrollbar-gutter: stable` rule reserves its width so there is no change in page width when the overflow
 * styling is applied; when hidden, that space is not reserved. If omitted, the value is read from the nearest
 * ScreenLockProvider, so a page/app can set the behavior once at its root and every screen lock beneath it
 * adopts it.
 * @param transitionDelayMs Optional delay in milliseconds to wait before checking if modals are still in the portal.
 * This allows CSS transitions to complete. Only used when provided.
 */
const useScreenLock = (isActive: boolean, hidePageScrollbarGutter?: boolean, transitionDelayMs?: number) => {
  const contextHidePageScrollbarGutter = useContext(ScreenLockContext);
  const shouldHidePageScrollbarGutter = hidePageScrollbarGutter ?? contextHidePageScrollbarGutter;
  const timeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);

  useEffect(() => {
    const htmlDocument = document.querySelector('html') as HTMLElement;

    if (isActive) {
      htmlDocument.classList.add('modalOpen');
      if (shouldHidePageScrollbarGutter) {
        htmlDocument.classList.add('hidePageScrollbarGutter');
      }
    }

    return () => {
      // Clear any existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      const checkAndRemoveClasses = () => {
        const count = document.getElementById('modalPortal')?.childElementCount;
        if (count === undefined || count < 1) {
          // Remove the class only if there are no more modals
          htmlDocument.classList.remove('modalOpen', 'hidePageScrollbarGutter');
        }
      };

      if (transitionDelayMs !== undefined) {
        // Use timeout logic when transition delay is provided
        timeoutRef.current = setTimeout(checkAndRemoveClasses, transitionDelayMs);
      } else {
        // Use immediate check when no transition delay is provided
        checkAndRemoveClasses();
      }
    };
  }, [shouldHidePageScrollbarGutter, isActive, transitionDelayMs]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);
};

export default useScreenLock;
